import { format, isThisYear, isToday, isYesterday, parseISO } from "date-fns";
import type { ComponentType, SVGProps } from "react";
import { useAuthStore } from "../../stores/auth-store";
import { useActivityLogs } from "../../hooks/useActivityLogs";
import type { ActivityLogScope } from "../../hooks/useActivityLogs";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { EmptyState } from "../ui/EmptyState";
import { Skeleton } from "../ui/Skeleton";
import { cn } from "../../lib/utils";
import { env } from "../../lib/env";
import type { ActivityAction, ActivityLogItem } from "../../types/activity";
import HistoryLinear from "~icons/solar/history-linear";
import DangerCircleLinear from "~icons/solar/danger-circle-linear";
import StarsLinear from "~icons/solar/stars-linear";
import StarsMinimalisticLinear from "~icons/solar/stars-minimalistic-linear";
import Chart2Linear from "~icons/solar/chart-2-linear";
import DocumentTextLinear from "~icons/solar/document-text-linear";
import LetterLinear from "~icons/solar/letter-linear";
import CalendarLinear from "~icons/solar/calendar-linear";
import RefreshLinear from "~icons/solar/refresh-linear";
import CloseCircleLinear from "~icons/solar/close-circle-linear";
import CheckCircleLinear from "~icons/solar/check-circle-linear";

// ---------------------------------------------------------------------------
// Action → tag / colour / node icon (v3 CandidateActivityLog.tag/tagColor/icon)
// ---------------------------------------------------------------------------

type TagColor = "primary" | "emerald" | "blue" | "amber" | "slate";
type IconType = ComponentType<SVGProps<SVGSVGElement>>;

interface ActionMeta {
  tag: string;
  color: TagColor;
  icon: IconType;
}

const ACTION_META: Partial<Record<ActivityAction, ActionMeta>> = {
  candidate_discovered: { tag: "Sourced", color: "primary", icon: StarsLinear },
  confidence_score_initial: { tag: "Scored", color: "blue", icon: Chart2Linear },
  cet_generated: { tag: "CET", color: "blue", icon: DocumentTextLinear },
  greeting_sent: { tag: "Outreach", color: "primary", icon: LetterLinear },
  meeting_scheduled: { tag: "Meeting", color: "amber", icon: CalendarLinear },
  meeting_rescheduled: { tag: "Meeting", color: "amber", icon: CalendarLinear },
  candidate_no_show: { tag: "No-show", color: "amber", icon: CalendarLinear },
  meeting_cancelled: { tag: "Meeting", color: "slate", icon: CalendarLinear },
  meeting_started: { tag: "Meeting", color: "emerald", icon: CalendarLinear },
  meeting_completed: { tag: "Meeting", color: "emerald", icon: CalendarLinear },
  status_changed: { tag: "Status", color: "slate", icon: RefreshLinear },
  candidate_rejected: { tag: "Rejected", color: "slate", icon: CloseCircleLinear },
  candidate_offered: { tag: "Offered", color: "emerald", icon: CheckCircleLinear },
  candidate_hired: { tag: "Hired", color: "emerald", icon: CheckCircleLinear },
  ai_decision_accepted: { tag: "AI Decision", color: "blue", icon: StarsMinimalisticLinear },
  ai_decision_overridden: { tag: "AI Decision", color: "amber", icon: StarsMinimalisticLinear },
};

const DEFAULT_META: ActionMeta = { tag: "Activity", color: "slate", icon: HistoryLinear };

const TAG_CLASSES: Record<TagColor, string> = {
  primary: "border-primary/20 bg-primary-light text-primary",
  emerald: "border-status-green-border bg-status-green-bg text-status-green-text",
  blue: "border-status-blue-border bg-status-blue-bg text-status-blue-text",
  amber: "border-status-gold-border bg-status-gold-bg text-status-gold-text",
  slate: "border-status-slate-border bg-status-slate-bg text-status-slate-text",
};

/** "Today, 2:45 PM" · "Yesterday, 9:10 AM" · "Sep 3, 2:45 PM" · "Sep 3, 2025, 2:45 PM". */
function formatWhen(iso: string): string {
  const d = parseISO(iso);
  if (isNaN(d.getTime())) return iso;
  const time = format(d, "h:mm a");
  if (isToday(d)) return `Today, ${time}`;
  if (isYesterday(d)) return `Yesterday, ${time}`;
  return `${format(d, isThisYear(d) ? "MMM d" : "MMM d, yyyy")}, ${time}`;
}

function actorLabel(item: ActivityLogItem, currentUserId: string | undefined): string {
  if (item.actor_id && currentUserId && item.actor_id === currentUserId) return "You";
  if (item.actor_name) return item.actor_name;
  if (item.actor_type === "ai") return "Dahling AI";
  if (item.actor_type === "candidate") return "Candidate";
  return "System";
}

// ---------------------------------------------------------------------------
// Visible trail — confidence-score rows are held behind the score feature flag
// ---------------------------------------------------------------------------

export function useActivityTrail(scope: ActivityLogScope) {
  const query = useActivityLogs(scope);
  const hidden = env.FEATURE_CONFIDENCE_SCORE_UI
    ? 0
    : query.items.filter((i) => i.action === "confidence_score_initial").length;
  const items = hidden > 0 ? query.items.filter((i) => i.action !== "confidence_score_initial") : query.items;
  return { ...query, items, total: Math.max(0, query.total - hidden) };
}

// ---------------------------------------------------------------------------
// Timeline entry
// ---------------------------------------------------------------------------

function ActivityEntry({
  item,
  first,
  currentUserId,
}: {
  item: ActivityLogItem;
  first: boolean;
  currentUserId: string | undefined;
}) {
  const meta = ACTION_META[item.action] ?? DEFAULT_META;
  const NodeIcon = meta.icon;
  const actor = actorLabel(item, currentUserId);
  const description =
    actor === "You" && item.actor_name ? item.description.split(item.actor_name).join("You") : item.description;

  return (
    <div className="group/item relative">
      {/* Timeline node */}
      <div
        className={cn(
          "absolute -left-6 top-0 flex size-6 items-center justify-center rounded-full border-2 shadow-xs sm:-left-8",
          first
            ? "border-white bg-primary text-white ring-4 ring-primary/10"
            : "border-status-slate-border bg-white text-ink-soft"
        )}
        aria-hidden="true"
      >
        <NodeIcon className="size-3" />
      </div>

      {/* Item card */}
      <div className="rounded-xl border border-black/[0.05] bg-surface-2 p-4 transition hover:border-black/10 hover:bg-white hover:shadow-sm">
        <div className="flex flex-col justify-between gap-1.5 border-b border-black/[0.04] pb-2 sm:flex-row sm:items-center">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-xs font-bold text-ink sm:text-sm">{item.title}</h4>
            <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-bold", TAG_CLASSES[meta.color])}>
              {meta.tag}
            </span>
            {item.project_name && (
              <span className="rounded-full border border-black/[0.06] bg-white px-2 py-0.5 text-[10px] font-semibold text-ink-muted">
                {item.project_name}
              </span>
            )}
          </div>
          <time dateTime={item.occurred_at} className="shrink-0 text-[11px] font-medium text-ink-faint">
            {formatWhen(item.occurred_at)}
          </time>
        </div>

        {description && <p className="mt-2 text-xs leading-relaxed text-ink-soft">{description}</p>}

        <div className="mt-2.5 flex items-center gap-2 text-[11px] text-ink-muted">
          <span className="text-ink-faint">Logged by:</span>
          <span className="inline-flex items-center gap-1 font-semibold text-ink">
            {item.actor_type === "ai" && (
              <StarsMinimalisticLinear aria-hidden="true" className="size-3 text-primary" />
            )}
            {actor}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component — v3 "Recruitment Activity Trail"
// ---------------------------------------------------------------------------

export interface ActivitiesLogProps {
  scope: ActivityLogScope;
}

export function ActivitiesLog({ scope }: ActivitiesLogProps) {
  const currentUser = useAuthStore((s) => s.user);
  const { items, total, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error, refetch } =
    useActivityTrail(scope);

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4" aria-label="Loading activity">
        <div className="flex items-center gap-3 rounded-2xl border border-black/[0.08] bg-white px-5 py-3.5 shadow-xs">
          <Skeleton className="size-9 rounded-xl" />
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
        </div>
        <Card padding="lg" className="rounded-2xl">
          <div className="flex flex-col gap-6 pl-6 sm:pl-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card padding="lg" className="mx-auto w-full max-w-4xl rounded-2xl">
        <EmptyState
          compact
          icon={DangerCircleLinear}
          title="Couldn't load activity"
          description={error}
          action={
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card padding="lg" className="mx-auto w-full max-w-4xl rounded-2xl">
        <EmptyState
          compact
          icon={HistoryLinear}
          title="No activity yet"
          description="Sourcing, outreach, meetings and status changes will show up here."
        />
      </Card>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
      {/* Header summary banner */}
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-black/[0.08] bg-white px-5 py-3.5 shadow-xs">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary-light text-primary">
            <HistoryLinear aria-hidden="true" className="size-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-xs font-bold uppercase tracking-wider text-ink">Recruitment Activity Trail</h3>
            <p className="text-xs text-ink-muted">
              {total} {total === 1 ? "event" : "events"} logged from candidate discovery to calibration
            </p>
          </div>
        </div>
        <span className="shrink-0 whitespace-nowrap rounded-full bg-status-slate-bg px-3 py-1 text-xs font-medium text-ink-muted">
          Sorted: Newest first
        </span>
      </div>

      {/* Timeline */}
      <Card padding="lg" className="rounded-2xl">
        <div className="relative flex flex-col gap-6 pl-6 before:absolute before:bottom-2 before:left-3 before:top-2 before:w-[2px] before:bg-gradient-to-b before:from-primary before:via-status-blue-border before:to-status-slate-border sm:pl-8">
          {items.map((item, idx) => (
            <ActivityEntry key={item.id} item={item} first={idx === 0} currentUserId={currentUser?.id} />
          ))}
        </div>
      </Card>

      {hasNextPage && (
        <div className="flex justify-center py-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void fetchNextPage()}
            loading={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading more…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}
